import { useState, useRef } from 'react';
import { useGT } from 'gt-react';
import type { SchemaFieldDefinition } from '@erp/shared-types';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import { Textarea } from 'src/components/ui/textarea';
import { Switch } from 'src/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';

interface DynamicSchemaEnumOption {
  value: string;
  label: string;
}

const EMPTY_SELECT_VALUE = '__none__';

interface DynamicSchemaFormProps {
  fields: SchemaFieldDefinition[];
  values: Record<string, unknown>;
  onChange: (values: Record<string, unknown>) => void;
  readOnly?: boolean;
  enumOptions?: Record<string, DynamicSchemaEnumOption[]>;
}

function resolveWidget(field: SchemaFieldDefinition): string {
  if (field.uiConfig?.widget) return field.uiConfig.widget;

  switch (field.fieldType) {
    case 'text':
    case 'json':
      return 'textarea';
    case 'boolean':
      return 'toggle';
    case 'integer':
    case 'float':
    case 'unit':
      return 'number';
    case 'currency':
      return 'currency';
    case 'date':
    case 'datetime':
      return 'date-picker';
    case 'enum':
      return 'select';
    case 'object':
      return 'object';
    case 'array':
      return 'array';
    default:
      return 'text';
  }
}

function widthClass(width?: 'sm' | 'md' | 'lg' | 'full'): string {
  switch (width) {
    case 'sm':
      return 'w-48';
    case 'md':
      return 'w-72';
    case 'lg':
      return 'w-96';
    case 'full':
      return 'w-full';
    default:
      return 'w-72';
  }
}

function parseNumericInput(value: string): number | null {
  if (value === '') {
    return null;
  }

  const parsedValue = Number(value);
  return Number.isNaN(parsedValue) ? null : parsedValue;
}

export function DynamicSchemaForm({
  fields,
  values,
  onChange,
  readOnly = false,
  enumOptions,
}: DynamicSchemaFormProps) {
  const sorted = [...fields].sort((a, b) => a.sortOrder - b.sortOrder);

  function handleFieldChange(fieldName: string, value: unknown) {
    onChange({ ...values, [fieldName]: value });
  }

  // Group fields by section
  const sections = new Map<string, SchemaFieldDefinition[]>();
  for (const field of sorted) {
    const section = field.uiConfig?.section ?? '';
    const group = sections.get(section) ?? [];
    group.push(field);
    sections.set(section, group);
  }

  return (
    <div className="space-y-4">
      {Array.from(sections.entries()).map(([section, sectionFields]) => (
        <div key={section || '__default'}>
          {section && (
            <h4 className="mb-2 text-sm font-medium text-muted-foreground">
              {section}
            </h4>
          )}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {sectionFields.map((field) => (
              <DynamicField
                key={field.id}
                field={field}
                value={values[field.fieldName]}
                onValueChange={(v) => handleFieldChange(field.fieldName, v)}
                readOnly={readOnly}
                enumOptions={enumOptions}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function DynamicField({
  field,
  value,
  onValueChange,
  readOnly,
  enumOptions,
}: {
  field: SchemaFieldDefinition;
  value: unknown;
  onValueChange: (v: unknown) => void;
  readOnly: boolean;
  enumOptions?: Record<string, DynamicSchemaEnumOption[]>;
}) {
  const gt = useGT();
  const widget = resolveWidget(field);
  const width = widthClass(field.uiConfig?.width);

  // Object fields render as nested fieldset
  if (widget === 'object') {
    const objValue = (value as Record<string, unknown>) ?? {};
    return (
      <fieldset className="col-span-full rounded border p-3 space-y-2">
        <legend className="text-sm font-medium px-1">
          {field.label}
          {field.isRequired && <span className="ml-1 text-red-500">*</span>}
        </legend>
        {field.description && (
          <p className="text-xs text-muted-foreground">{field.description}</p>
        )}
        {field.children && field.children.length > 0 && (
          <DynamicSchemaForm
            fields={field.children}
            values={objValue}
            onChange={(newObj) => onValueChange(newObj)}
            readOnly={readOnly}
            enumOptions={enumOptions}
          />
        )}
      </fieldset>
    );
  }

  // Array fields render as repeatable section
  if (widget === 'array') {
    return (
      <ArrayField
        field={field}
        value={value}
        onValueChange={onValueChange}
        readOnly={readOnly}
        enumOptions={enumOptions}
      />
    );
  }

  return (
    <div className={`space-y-1 ${width}`}>
      <Label htmlFor={field.id}>
        {field.label}
        {field.isRequired && <span className="ml-1 text-red-500">*</span>}
      </Label>
      {field.description && (
        <p className="text-xs text-muted-foreground">{field.description}</p>
      )}
      {renderWidget(
        widget,
        field,
        value,
        onValueChange,
        readOnly,
        enumOptions,
        gt,
      )}
    </div>
  );
}

function ArrayField({
  field,
  value,
  onValueChange,
  readOnly,
  enumOptions,
}: {
  field: SchemaFieldDefinition;
  value: unknown;
  onValueChange: (v: unknown) => void;
  readOnly: boolean;
  enumOptions?: Record<string, DynamicSchemaEnumOption[]>;
}) {
  const gt = useGT();
  const items = (Array.isArray(value) ? value : []) as Record<
    string,
    unknown
  >[];

  // Stable keys for array items to avoid index-as-key reconciliation bugs
  const nextKeyRef = useRef(items.length);
  const [itemKeys, setItemKeys] = useState<number[]>(() =>
    items.map((_, i) => i),
  );

  // Sync keys length with items length (handles external value changes)
  if (itemKeys.length !== items.length) {
    const newKeys = items.map((_, i) =>
      i < itemKeys.length ? itemKeys[i] : nextKeyRef.current++,
    );
    setItemKeys(newKeys);
  }

  function addItem() {
    setItemKeys([...itemKeys, nextKeyRef.current++]);
    onValueChange([...items, {}]);
  }

  function removeItem(index: number) {
    setItemKeys(itemKeys.filter((_, i) => i !== index));
    onValueChange(items.filter((_, i) => i !== index));
  }

  function updateItem(index: number, newValue: Record<string, unknown>) {
    const updated = [...items];
    updated[index] = newValue;
    onValueChange(updated);
  }

  return (
    <fieldset className="col-span-full rounded border p-3 space-y-2">
      <legend className="text-sm font-medium px-1">
        {field.label}
        {field.isRequired && <span className="ml-1 text-red-500">*</span>}
      </legend>
      {field.description && (
        <p className="text-xs text-muted-foreground">{field.description}</p>
      )}
      {items.map((item, index) => (
        <div key={itemKeys[index]} className="rounded border p-2 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              {gt('Item {n}', {
                context: 'label for an array item in a dynamic form',
                n: index + 1,
              })}
            </span>
            {!readOnly && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 text-xs"
                onClick={() => removeItem(index)}
              >
                {gt('Remove', {
                  context:
                    'button to remove an item from an array field in a dynamic form',
                })}
              </Button>
            )}
          </div>
          {field.children && field.children.length > 0 && (
            <DynamicSchemaForm
              fields={field.children}
              values={item}
              onChange={(newObj) => updateItem(index, newObj)}
              readOnly={readOnly}
              enumOptions={enumOptions}
            />
          )}
        </div>
      ))}
      {!readOnly && (
        <Button variant="outline" size="sm" onClick={addItem}>
          {gt('Add Item', {
            context:
              'button to add an item to an array field in a dynamic form',
          })}
        </Button>
      )}
    </fieldset>
  );
}

function renderWidget(
  widget: string,
  field: SchemaFieldDefinition,
  value: unknown,
  onValueChange: (v: unknown) => void,
  readOnly: boolean,
  enumOptions: Record<string, DynamicSchemaEnumOption[]> | undefined,
  gt: ReturnType<typeof useGT>,
) {
  switch (widget) {
    case 'textarea':
      return (
        <Textarea
          id={field.id}
          value={String(value ?? '')}
          onChange={(e) => onValueChange(e.target.value)}
          placeholder={field.uiConfig?.placeholder}
          disabled={readOnly}
          rows={3}
        />
      );
    case 'toggle':
      return (
        <Switch
          id={field.id}
          checked={Boolean(value)}
          onCheckedChange={onValueChange}
          disabled={readOnly}
        />
      );
    case 'number':
      return (
        <Input
          id={field.id}
          type="number"
          value={value != null ? String(value) : ''}
          onChange={(e) => onValueChange(parseNumericInput(e.target.value))}
          placeholder={field.uiConfig?.placeholder}
          disabled={readOnly}
        />
      );
    case 'date-picker':
      return (
        <Input
          id={field.id}
          type={field.fieldType === 'datetime' ? 'datetime-local' : 'date'}
          value={String(value ?? '')}
          onChange={(e) => onValueChange(e.target.value)}
          disabled={readOnly}
        />
      );
    case 'select': {
      // Adhoc enum values take precedence over coreEnumId options
      let options: DynamicSchemaEnumOption[] = [];
      if (field.enumValues && field.enumValues.length > 0) {
        options = field.enumValues.map((v) => ({ value: v, label: v }));
      } else if (field.coreEnumId) {
        options = enumOptions?.[field.coreEnumId] ?? [];
      }

      return (
        <Select
          value={
            value == null || value === '' ? EMPTY_SELECT_VALUE : String(value)
          }
          onValueChange={(nextValue) =>
            onValueChange(nextValue === EMPTY_SELECT_VALUE ? null : nextValue)
          }
          disabled={readOnly}
        >
          <SelectTrigger>
            <SelectValue
              placeholder={
                field.uiConfig?.placeholder ??
                gt('Select...', {
                  context:
                    'default placeholder for a select field in a dynamic form',
                })
              }
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={EMPTY_SELECT_VALUE}>
              {gt('None', {
                context:
                  'empty/null option in a select field in a dynamic form',
              })}
            </SelectItem>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }
    case 'currency':
      return (
        <Input
          id={field.id}
          type="number"
          step="0.01"
          value={value != null ? String(value) : ''}
          onChange={(e) => onValueChange(parseNumericInput(e.target.value))}
          placeholder={field.uiConfig?.placeholder ?? '0.00'}
          disabled={readOnly}
        />
      );
    default:
      return (
        <Input
          id={field.id}
          type="text"
          value={String(value ?? '')}
          onChange={(e) => onValueChange(e.target.value)}
          placeholder={field.uiConfig?.placeholder}
          disabled={readOnly}
        />
      );
  }
}
